import api from './api';
import { AxiosResponse } from 'axios';
import {
  LengthPhysicalUnit,
  TemperaturePhysicalUnit,
  PressurePhysicalUnit,
  VelocityPhysicalUnit,
  VolumetricFlowRatePhysicalUnit,
  ThermalPowerPhysicalUnit,
  HeatTransferCoefficientPhysicalUnit,
  RatioPhysicalUnit,
  DropDownLengthPhysicalUnit,
  OptionalLengthPhysicalUnit,
  OptionalTemperaturePhysicalUnit,
  OptionalPressurePhysicalUnit,
  OptionalVolumetricFlowRatePhysicalUnit,
  OptionalThermalPowerPhysicalUnit,

  OptionalRatioPhysicalUnit,
  OptionalDensityPhysicalUnit,
  OptionalDynamicViscosityPhysicalUnit,
  OptionalSpecificHeatCapacityPhysicalUnit,
  OptionalThermalConductivityPhysicalUnit,
  OptionalSpecificThermalResistancePhysicalUnit
} from '../components/common/types/physicalUnits';

// Type definitions
export interface TubeUI {
  id: string;
  name: string;
  tube_number: string;
  material: string;
  tube_family: string;
  outer_diameter: LengthPhysicalUnit;
  wall_thickness: DropDownLengthPhysicalUnit;
  refrigerants: string[];
  diameter_category: string;
}

export interface TubeDropdownValuesAPI {
  tube_family?: string[] | null;
  material?: string[] | null;
  diameter_category?: string[] | null;
  wall_thickness?: DropDownLengthPhysicalUnit[] | null;
  refrigerant?: string[] | null;
  brand_name?: string[] | null;
  suitable_for_fouling?: boolean[] | null;
}

export interface SelectionLabelsCondensation {
  calculation_mode: string[];
  unit_system: string[];
  message_level: string[];
  refrigerant: string[];
  ratio_unit: string[];
  medium_type: string[];
  thermal_power_unit: string[];
  pressure_unit: string[];
  velocity_unit: string[];
  volumetric_flow_unit: string[];
  thermal_resistance_unit: string[];
  temperature_unit: string[];
  temperature_difference_unit: string[];
  heattransfer_coefficient_unit: string[];
  length_unit: string[];
  heatflux_unit: string[];
  specific_heat_capacity_unit: string[];
  dynamic_viscosity_unit: string[];
  density_unit: string[];
  thermal_conductivity_unit: string[];
  subcooling_type: string[];
  mass_flow_unit: string[];
  tube_arrangement: string[];
}

export interface OptionalRequestBodyCondenserAPI {
  condenser_configuration?: OptionalCondenserConfigurationAPI | null;
  fluids?: OptionalCondenserFluidsAPI | null;
  system_of_units: string;
  operating_conditions?: CondensationOperatingConditionsAPI | null;
  interchangable_input_conditions?: InterChangableOperatingConditionsCondenserAPI | null;
  calculation_mode?: string | null;
  correction_factor_outside?: number | null;
  correction_factor_inside?: number | null;
}

export interface OptionalCondenserConfigurationAPI {
  tube_count?: number | null;
  number_of_passes?: number | null;
  number_of_tube_rows?: number | null;
  override_tube_rows?: boolean | null;
  tube_arrangement?: string | null;
  plain_segments?: OptionalPlainSegmentsAPI | null;
  tube_name?: string | null;
}

export interface OptionalPlainSegmentsAPI {
  inner_segments?: number | null;
  length_per_end_segment?: OptionalLengthPhysicalUnit | null;
  length_per_inner_segment?: OptionalLengthPhysicalUnit | null;
}

export interface OptionalCondenserFluidsAPI {
  refrigerant?: string | null;
  medium?: OptionalMediumAPI | null;
  fouling_factor_inside?: OptionalSpecificThermalResistancePhysicalUnit | null;
  fouling_factor_outside?: OptionalSpecificThermalResistancePhysicalUnit | null;
}

export interface OptionalMediumAPI {
  name?: string | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
  custom_name?: string | null;
  reference_temperature?: OptionalTemperaturePhysicalUnit | null;
  custom_density?: OptionalDensityPhysicalUnit | null;
  custom_dynamic_viscosity?: OptionalDynamicViscosityPhysicalUnit | null;
  custom_specific_heat_capacity?: OptionalSpecificHeatCapacityPhysicalUnit | null;
  custom_thermal_conductivity?: OptionalThermalConductivityPhysicalUnit | null;
}

export interface CondensationOperatingConditionsAPI {
  sub_cooling: TemperaturePhysicalUnit;
  sub_cooling_type: string;
  temperature_evaporation?: OptionalTemperaturePhysicalUnit | null;
  temperature_suction_gas?: OptionalTemperaturePhysicalUnit | null;
  temperature_superheated_vapour?: OptionalTemperaturePhysicalUnit | null;
  override_temperature_superheated_vapour: boolean;
}

export interface InterChangableOperatingConditionsCondenserAPI {
  temperature_medium_inlet?: OptionalTemperaturePhysicalUnit | null;
  temperature_medium_outlet?: OptionalTemperaturePhysicalUnit | null;
  capacity?: OptionalThermalPowerPhysicalUnit | null;
  volumetric_flow_rate?: OptionalVolumetricFlowRatePhysicalUnit | null;
  temperature_saturation_condensation?: OptionalTemperaturePhysicalUnit | null;
  tube_length?: OptionalLengthPhysicalUnit | null;
}
export interface MediumResultAPI {
  name: string;
  mean_temperature: OptionalTemperaturePhysicalUnit | null;
  thermal_conductivity: OptionalThermalConductivityPhysicalUnit | null;
  density: OptionalDensityPhysicalUnit | null;
  dynamic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  kinematic_viscosity: OptionalDynamicViscosityPhysicalUnit | null;
  specific_heat_capacity: OptionalSpecificHeatCapacityPhysicalUnit | null;
  prandtl_number: number | null;
  brine_concentration?: OptionalRatioPhysicalUnit | null;
}

export interface RefrigerantResultAPI {
  name: string;
  saturation_pressure: OptionalPressurePhysicalUnit | null;
  critical_temperature: OptionalTemperaturePhysicalUnit | null;
  critical_pressure: OptionalPressurePhysicalUnit | null;
}

export interface ResultBodyCondenserAPI {
  input_data?: OptionalRequestBodyCondenserAPI | null;
  messages?: Message[] | null;
  overall_message_level?: string | null;
  capacity?: ThermalPowerPhysicalUnit | null;
  distance_upper_row?: LengthPhysicalUnit | null;
  volumetric_flow_rate?: VolumetricFlowRatePhysicalUnit | null;
  free_space_above?: RatioPhysicalUnit | null;
  free_space_beneath?: RatioPhysicalUnit | null;
  horizontal_tube_pitch?: LengthPhysicalUnit | null;
  overall_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inner_shell_diameter?: LengthPhysicalUnit | null;
  mediumside_pressure_drop?: PressurePhysicalUnit | null;
  mediumside_pressure_drop_total?: PressurePhysicalUnit | null;
  inside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  inside_temperature_mean?: TemperaturePhysicalUnit | null;
  inside_velocity?: VelocityPhysicalUnit | null;
  lmtd?: TemperaturePhysicalUnit | null;
  outside_heat_transfer_coefficient?: HeatTransferCoefficientPhysicalUnit | null;
  outside_heat_flux?: ThermalPowerPhysicalUnit | null;
  over_design?: RatioPhysicalUnit | null;
  inside_fouling_resistance?: RatioPhysicalUnit | null;
  outside_fouling_resistance?: RatioPhysicalUnit | null;
  inside_htc_resistance?: RatioPhysicalUnit | null;
  outside_htc_resistance?: RatioPhysicalUnit | null;
  wall_resistance?: RatioPhysicalUnit | null;
  reynolds_number?: number | null;
  temperature_medium_inlet?: TemperaturePhysicalUnit | null;
  temperature_medium_outlet?: TemperaturePhysicalUnit | null;
  tube_length?: LengthPhysicalUnit | null;
  tube_rows?: number | null;
  tubes_upper_row?: number | null;
  vertical_tube_pitch?: LengthPhysicalUnit | null;
  heat_transfer_area_outside?: LengthPhysicalUnit | null;
  medium?: MediumResultAPI | null;
  refrigerant?: RefrigerantResultAPI | null;
  bundle_factor?: number | null;
  temperature_superheated_vapour?: TemperaturePhysicalUnit | null;
  temperature_saturation_condensation?: TemperaturePhysicalUnit | null;
  natural_sub_cooling?: TemperaturePhysicalUnit | null;
  sub_cooling?: TemperaturePhysicalUnit | null;
  number_of_sub_cooling_tubes?: number | null;
}

export interface ExtendedResultBodySpecialCondenserAPI extends ResultBodyCondenserAPI {
  timestamp?: string;
}

export interface Message {
  message_level: string;
  message_content: string;
}


/**
 * Comprehensive service for the Special Condenser calculator
 * Combines both UI and calculation functionality
 */
const specialCondenserService = {
  // ======================================================
  // UI Related API Endpoints
  // ======================================================
  
  /**
   * Retrieves selection labels for the UI
   */
  getInputLabels: (): Promise<AxiosResponse<SelectionLabelsCondensation>> => 
    api.get<SelectionLabelsCondensation>('/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/input-labels'),

  /**
   * Checks if a tube exists in the database
   * @param tubeName Name of the tube to check
   */
  checkTubeInDB: (tubeName: string, unitSystem: string): Promise<AxiosResponse<TubeUI>> => 
    api.get<TubeUI>(`/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/tubes/is-tube-in-db?tube_name=${encodeURIComponent(tubeName)}&unit_system=${unitSystem}`),

  /**
   * Searches for tubes by name or criteria
   * @param searchCriteria Search string
   * @param unitSystem Unit system to use for returned values
   */
  getTubesByName: (searchCriteria: string, unitSystem: string): Promise<AxiosResponse<TubeUI[]>> => 
    api.get<TubeUI[]>(`/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/tubes?search_criteria=${searchCriteria}&unit_system=${unitSystem}`),

  /**
   * Gets unique tube values for dropdown filters
   * @param unitSystem Unit system to use
   */
  getUniqueTubeValues: (unitSystem: string): Promise<AxiosResponse<TubeDropdownValuesAPI>> => 
    api.get<TubeDropdownValuesAPI>(`/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/tubes/unique-values?unit_system=${unitSystem}`),

  /**
   * Advanced query for tubes with multiple filter criteria
   * @param params Query parameters
   */
  queryTubes: (params: {
    unit_system: string,
    tube_family?: string | null,
    material?: string | null,
    diameter_category?: string | null,
    wall_thickness_key?: string | null,
    suitable_for_fouling?: boolean | null,
    refrigerant?: string | null,
    search_criteria?: string | null
  }): Promise<AxiosResponse<TubeUI[]>> => {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
    
    return api.get<TubeUI[]>(`/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/tubes/query?${queryParams.toString()}`);
  },

  /**
   * Gets filtered dropdown values for tubes based on current filter selections
   * This enables cascading filters for better user experience
   * @param currentFilters Current filter selections
   * @param unitSystem Unit system to use
   */
  getFilteredTubeDropdownValues: (currentFilters: {
    tube_family?: string | null,
    material?: string | null,
    diameter_category?: string | null,
    wall_thickness_key?: string | null,
    refrigerant?: string | null,
    brand_name?: string | null,
    suitable_for_fouling?: boolean | null
  }, unitSystem: string): Promise<AxiosResponse<TubeDropdownValuesAPI>> => {
    // Send filters directly as they match the TubeQuery schema (individual values, not arrays)
    return api.post<TubeDropdownValuesAPI>(
      `/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/tubes/filtered-dropdown-values?unit_system=${unitSystem}`,
      currentFilters
    );
  },

  /**
   * Converts values between unit systems
   * @param formValues Form values to convert
   */
  convertValues: (formValues: OptionalRequestBodyCondenserAPI): Promise<AxiosResponse<OptionalRequestBodyCondenserAPI>> => 
    api.post<OptionalRequestBodyCondenserAPI>('/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/convert-values', formValues),

  /**
   * Maps a payload to the input model
   * @param payload Payload to map
   */
  mapPayloadToInputModel: (payload: any): Promise<AxiosResponse<OptionalRequestBodyCondenserAPI>> => 
    api.post<OptionalRequestBodyCondenserAPI>('/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/map-payload-to-input-model', payload),

  // ======================================================
  // Calculation API Endpoints
  // ======================================================
  
  /**
   * Performs the condenser calculation
   * @param data Calculation input data
   */
  calculateCondenser: (data: OptionalRequestBodyCondenserAPI): Promise<AxiosResponse<ResultBodyCondenserAPI>> => 
    api.post<ResultBodyCondenserAPI>('/v1/heat-exchanger/shell-and-tube/condenser/tube/calculate', data),

  // ======================================================
  // Export API Endpoints
  // ======================================================
  
  /**
   * Generates Excel results file
   * @param results Results to export
   * @param name Optional filename
   */
  generateExcelResults: (
    results: ResultBodyCondenserAPI[], 
    name: string = 'special_condenser_results'
  ): Promise<AxiosResponse<Blob>> => 
    api.post<Blob>(
      '/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/download/results-excel-file',
      results,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    ),

  /**
   * Generates a PDF report
   * @param result Result to generate PDF for
   * @param projectInfo Optional project information
   * @param name Optional filename
   * @param share_url Optional share URL generated by frontend
   */
  generatePDF: (result: ResultBodyCondenserAPI, projectInfo?: any, name: string = 'special_condenser_results', share_url?: string): Promise<AxiosResponse<Blob>> => {
    const params = new URLSearchParams();
    if (name) params.append('name', name);
    
    const payload = {
      result,
      project_info: projectInfo,
      share_url
    };
    
    return api.post<Blob>(
      `/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/download/pdf?${params.toString()}`,
      payload,
      {
        responseType: 'blob',
        headers: {
          'Accept': 'application/pdf'
        }
      }
    );
  },

  convertTDUFile: (tduData: any): Promise<AxiosResponse<OptionalRequestBodyCondenserAPI>> => 
    api.post<OptionalRequestBodyCondenserAPI>('/v1/heat-exchanger/shell-and-tube/condenser/tube/ui/convert-tdu', tduData),
  
};

export default specialCondenserService; 